import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  GridApiPremium,
  GridRowModel,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid-premium';
import { debounce } from 'lodash';
import Actions from '../../store/Actions';
import {
  HoldingsUpdateRequest,
  IllioSearchOption,
  Currency,
  InstrumentType,
} from '../../types/generatedTypes';
import { showSnackbar } from '../../context/Snackbar/SnackbarContext';
import { SnackbarType } from '../../context/Snackbar/Types';
import { HoldingsDataTable } from '../../types/generatedTypes';
import { useGroups } from '../../hooks/useGroups';
import { useGridSelectionHandler } from '../../components/Groups/useGridSelection';
import { useExpandGroups } from '../../components/Groups/groupUtils';

interface UseHoldingsDataProps {
  apiRef: React.RefObject<GridApiPremium | null>;
  displayData: HoldingsDataTable[];
}

export const useHoldingsData = ({
  apiRef,
  displayData,
}: UseHoldingsDataProps) => {
  const [holdingsData, setHoldingsData] = useState<HoldingsDataTable[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [autosizeColumns, setAutosizeColumns] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});

  // Illio instrument search state
  const [searchOptions, setSearchOptions] = useState<
    Record<string, IllioSearchOption[]>
  >({});
  const [searchLoadingRows, setSearchLoadingRows] = useState<
    Record<string, boolean>
  >({});

  // Groups functionality
  const {
    groups,
    setGroups,
    isGroupingEnabled,
    setIsGroupingEnabled,
    isGroupsModalOpen,
    loadGroupsFromBackend,
    handleOpenGroupsModal,
    handleCloseGroupsModal,
    handleSaveGroups,
  } = useGroups({ pageType: 'holdings' });

  // Column fields configuration - must match actual columns in useHoldingsColumns
  const columnFields = useMemo(
    () => [
      'instrument_type',
      'label',
      'isin',
      'symbol',
      'currency',
      'multiplier',
    ],
    []
  );

  // Handle autosize state changes
  useEffect(() => {
    if (autosizeColumns && apiRef.current) {
      apiRef.current.autosizeColumns({
        includeHeaders: true,
        includeOutliers: true,
        expand: true,
        columns: columnFields,
      });
    } else if (!autosizeColumns && apiRef.current) {
      // Reset column widths to allow flex to work
      columnFields.forEach(field => {
        apiRef.current?.setColumnWidth(field, 0);
      });
    }
  }, [autosizeColumns, apiRef, columnFields]);

  const fetchHoldingsData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await Actions.getHoldings();

      // Data already has id field which MUI DataGrid uses
      const transformedData: HoldingsDataTable[] = response.holdings;

      setHoldingsData(transformedData);
    } catch (error) {
      console.error('Failed to fetch holdings data:', error);
      showSnackbar({
        title: 'Error',
        message: 'Failed to load holdings data',
        type: SnackbarType.error,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Original processRowUpdate function (for undo/redo wrapping)
  const originalProcessRowUpdate = useCallback(
    async (
      newRow: GridRowModel,
      oldRow?: GridRowModel
    ): Promise<HoldingsDataTable> => {
      try {
        // If oldRow is provided, compare values to check if anything actually changed
        if (oldRow) {
          const hasChanges = Object.keys(newRow).some(key => {
            // Skip comparing the id field and any undefined values
            if (key === 'id') return false;
            return newRow[key] !== oldRow[key];
          });

          // If no changes detected, return the original row without API call
          if (!hasChanges) {
            return newRow as HoldingsDataTable;
          }
        }

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { id, _id, original_multiplier, ...updatedHolding } =
          newRow as HoldingsUpdateRequest & {
            id?: string;
            _id?: string;
            original_multiplier?: number | null;
          };

        const response = await Actions.updateHolding(newRow.id, updatedHolding);

        // Update local state with the response from backend
        const updatedRow = {
          ...newRow,
          ...response.holding,
        } as HoldingsDataTable;
        setHoldingsData(prev =>
          prev.map(holding => (holding.id === newRow.id ? updatedRow : holding))
        );

        return updatedRow;
      } catch (error) {
        console.error('Error updating holding:', error);
        showSnackbar({
          title: 'Error',
          type: SnackbarType.error,
          message: 'Failed to update holding',
        });
        throw error;
      }
    },
    []
  );

  const handleProcessRowUpdateError = useCallback((error: Error) => {
    console.error('Row update error:', error);
    showSnackbar({
      title: 'Update failed',
      type: SnackbarType.error,
      message: error.message || 'Failed to update holding',
    });
  }, []);

  // Illio instrument search - debounced
  const handleIllioSearch = useMemo(
    () =>
      debounce(async (rowId: string, query: string) => {
        if (query.length < 2) {
          setSearchOptions(prev => ({ ...prev, [rowId]: [] }));
          return;
        }

        setSearchLoadingRows(prev => ({ ...prev, [rowId]: true }));
        try {
          const response = await Actions.searchWizardInstruments(query);
          const options = response.options || [];
          setSearchOptions(prev => ({
            ...prev,
            [rowId]: options,
          }));
        } catch (error) {
          console.error('Illio search failed:', error);
          setSearchOptions(prev => ({ ...prev, [rowId]: [] }));
        } finally {
          setSearchLoadingRows(prev => ({ ...prev, [rowId]: false }));
        }
      }, 500),
    []
  );

  // Get available options for a row (current selection + search results)
  // Plain function (not memoized) to avoid causing column recreation when searchOptions changes
  const getOptionsForRow = (rowId: string) => {
    const options: IllioSearchOption[] = [];

    // Add current selection as an option if it exists
    const currentHolding = holdingsData.find(h => h.id === rowId);
    if (currentHolding?.label && currentHolding?.source_id) {
      options.push({
        id: currentHolding.source_id,
        source_data: currentHolding.source_data || '',
        name: currentHolding.symbol_name || '',
        code: currentHolding.code || null,
        currency_code: currentHolding.currency || '',
        label: currentHolding.label,
        multiplier: currentHolding.multiplier,
        instrument_type: currentHolding.instrument_type ?? null,
      });
    }

    // Add search results (deduplicated)
    const searchResults = searchOptions[rowId] || [];
    searchResults.forEach(opt => {
      if (!options.find(o => o.id === opt.id)) {
        options.push(opt);
      }
    });

    return options;
  };

  // Handle Illio instrument selection - update holding
  const handleIllioInstrumentSelect = useCallback(
    async (holdingId: string, selectedInstrument: IllioSearchOption | null) => {
      try {
        if (selectedInstrument) {
          // Optimistic update - update UI immediately to avoid visual glitch
          setHoldingsData(prev =>
            prev.map(holding =>
              holding.id === holdingId
                ? {
                    ...holding,
                    label: selectedInstrument.label,
                    source_id: selectedInstrument.id,
                    source_data: selectedInstrument.source_data,
                    code: selectedInstrument.code,
                    currency: selectedInstrument.currency_code as Currency,
                    multiplier: selectedInstrument.multiplier,
                    original_multiplier: selectedInstrument.multiplier, // Set original to Illio value
                    instrument_type:
                      selectedInstrument.instrument_type as InstrumentType,
                  }
                : holding
            )
          );

          // Persist to backend - backend will set original_multiplier when source_id is set
          const updatePayload: HoldingsUpdateRequest = {
            label: selectedInstrument.label,
            source_id: selectedInstrument.id,
            source_data: selectedInstrument.source_data,
            code: selectedInstrument.code,
            currency: selectedInstrument.currency_code as Currency,
            multiplier: selectedInstrument.multiplier,
            instrument_type:
              selectedInstrument.instrument_type as InstrumentType,
          };

          await Actions.updateHolding(holdingId, updatePayload);

          showSnackbar({
            title: 'Success',
            type: SnackbarType.success,
            message: `Linked to Illio instrument: ${selectedInstrument.label}`,
          });
        } else {
          // Optimistic update - clear UI immediately to avoid visual glitch
          setHoldingsData(prev =>
            prev.map(holding =>
              holding.id === holdingId
                ? {
                    ...holding,
                    label: null,
                    source_id: null,
                    source_data: null,
                  }
                : holding
            )
          );

          // Then persist to backend
          const updatePayload: HoldingsUpdateRequest = {
            label: null,
            source_id: null,
            source_data: null,
          };

          await Actions.updateHolding(holdingId, updatePayload);
        }
      } catch (error) {
        console.error('Error updating Illio instrument:', error);
        showSnackbar({
          title: 'Error',
          type: SnackbarType.error,
          message: 'Failed to update Illio instrument',
        });
      }
    },
    []
  );

  // Export data handler
  const handleExport = useCallback(() => {
    if (apiRef.current) {
      apiRef.current.exportDataAsCsv();
    }
  }, [apiRef]);

  // Create expandGroups wrapper with current context
  const expandGroups = useExpandGroups(groups, displayData);

  // Row selection handler with group support using shared utility
  const handleRowSelectionChange = useGridSelectionHandler(
    selectedRows,
    setSelectedRows,
    displayData,
    {
      getRowId: row => row.id.toString(),
      expandGroups,
    }
  );

  // Apply column visibility
  const applyColumnVisibility = useCallback(
    (visibilityModel: GridColumnVisibilityModel) => {
      if (apiRef.current) {
        apiRef.current.setColumnVisibilityModel(visibilityModel);
      }
      setColumnVisibilityModel(visibilityModel);
    },
    [apiRef]
  );

  // Prepare available rows for groups modal
  const availableRowsForGroups = useMemo(
    () =>
      holdingsData.map(row => ({
        id: row.id, // Keep the original id for the groups modal
        _id: row.id, // Also provide _id for compatibility with group utilities
        label: `${row.symbol || 'Unknown'} - ${row.symbol_name || 'No Name'}`,
        symbol: row.symbol,
        symbol_name: row.symbol_name,
        instrument_type: row.instrument_type,
        currency: row.currency,
        multiplier: row.multiplier,
      })),
    [holdingsData]
  );

  return {
    holdingsData,
    loading,
    selectedRows,
    autosizeColumns,
    setAutosizeColumns,
    columnVisibilityModel,
    setColumnVisibilityModel,
    applyColumnVisibility,
    columnFields,
    fetchHoldingsData,
    originalProcessRowUpdate,
    handleProcessRowUpdateError,
    handleExport,
    handleRowSelectionChange,
    expandGroups,
    // Illio instrument search
    searchOptions,
    searchLoadingRows,
    handleIllioSearch,
    getOptionsForRow,
    handleIllioInstrumentSelect,
    // Groups
    groups,
    setGroups,
    loadGroupsFromBackend,
    isGroupsModalOpen,
    isGroupingEnabled,
    setIsGroupingEnabled,
    availableRowsForGroups,
    handleOpenGroupsModal,
    handleCloseGroupsModal,
    handleSaveGroups,
  };
};
